<template>
  <div class="editor">
    <editor-menu-bar :editor="editor" v-slot="{commands, isActive, focused}">
      <div class="menubar is-hidden" :class="{'is-focused': focused}">
        <div
          class="menubar-button"
          :class="{'is-active': isActive.bold()}"
          @click="commands.bold"
        >
          <Sprite name="bold" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.italic()}"
          @click="commands.italic"
        >
          <Sprite name="italic" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.strike()}"
          @click="commands.strike"
        >
          <Sprite name="strike" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.underline()}"
          @click="commands.underline"
        >
          <Sprite name="underline" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.code()}"
          @click="commands.code"
        >
          <Sprite name="code" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.paragraph()}"
          @click="commands.paragraph"
        >
          <Sprite name="paragraph" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.heading({level: 1})}"
          @click="commands.heading({level: 1})"
        >
          <span>H1</span>
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.heading({level: 2})}"
          @click="commands.heading({level: 2})"
        >
          <span>H2</span>
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.heading({level: 3})}"
          @click="commands.heading({level: 3})"
        >
          <span>H3</span>
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.bullet_list()}"
          @click="commands.bullet_list"
        >
          <Sprite name="ul" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.ordered_list()}"
          @click="commands.ordered_list"
        >
          <Sprite name="ol" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.blockquote()}"
          @click="commands.blockquote"
        >
          <Sprite name="quote" />
        </div>

        <div
          class="menubar-button"
          :class="{'is-active': isActive.code_block()}"
          @click="commands.code_block"
        >
          <Sprite name="code" />
        </div>

        <div
          class="menubar-button"
          @click="
            commands.createTable({
              rowsCount: 3,
              colsCount: 3,
              withHeaderRow: false
            })
          "
        >
          <Sprite name="table" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.deleteTable">
          <Sprite name="delete_table" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.addColumnBefore">
          <Sprite name="add_col_before" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.addColumnAfter">
          <Sprite name="add_col_after" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.deleteColumn">
          <Sprite name="delete_col" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.addRowBefore">
          <Sprite name="add_row_before" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.addRowAfter">
          <Sprite name="add_row_after" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.deleteRow">
          <Sprite name="delete_row" />
        </div>

        <div v-if="isActive.table()" class="menubar-button" @click="commands.toggleCellMerge">
          <Sprite name="combine_cells" />
        </div>

        <div v-if="allowImages" class="menubar-button" @click="showImagePrompt(commands.image)">
          <Sprite name="photo" />
        </div>

        <div class="menubar-button" @click="commands.horizontal_rule">
          <Sprite name="hr" />
        </div>

        <div class="menubar-button" @click="commands.undo">
          <Sprite name="undo" />
        </div>

        <div class="menubar-button" @click="commands.redo">
          <Sprite name="redo" />
        </div>
      </div>
    </editor-menu-bar>

    <editor-content class="editor-content" :editor="editor" />
  </div>
</template>

<script>
import Sprite from '@/components/Sprite'
import {Editor, EditorContent, EditorMenuBar} from 'tiptap'
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Table,
  TableHeader,
  TableCell,
  TableRow,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Image
} from 'tiptap-extensions'

export default {
  props: ['value', 'allowImages'],

  components: {
    EditorContent,
    EditorMenuBar,
    Sprite
  },

  data() {
    return {
      editor: null,
      editorChange: false
    }
  },

  beforeDestroy() {
    this.editor.destroy()
  },

  mounted() {
    let newEditor = new Editor({
      extensions: [
        new Blockquote(),
        new BulletList(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({levels: [1, 2, 3]}),
        new HorizontalRule(),
        new ListItem(),
        new OrderedList(),
        new TodoItem(),
        new TodoList(),
        new Link(),
        new Bold(),
        new Code(),
        new Table({
          resizable: false
        }),
        new TableHeader(),
        new Image(),
        new TableCell(),
        new TableRow(),
        new Italic(),
        new Strike(),
        new Underline(),
        new History()
      ],
      // content: this.value,
      onUpdate: ({getHTML}) => {
        this.editorChange = true
        this.$emit('input', getHTML())
      }
    })

    this.editor = newEditor
    this.editor.setContent(this.value)
  },

  methods: {
    showImagePrompt(command) {
      const src = prompt('Enter the url of your image here. We suggest uploading it to postimage.com.')
      if (src !== null) {
        command({src})
      }
    }
  },

  watch: {
    value(val) {
      // so cursor doesn't jump to start on typing
      if (this.editor && !this.editorChange) {
        this.editor.setContent(val, true)
      }
      this.editorChange = false
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/base/theme'
@import '../stylesheets/helpers/mixins'

.editor
  &:hover
    .menubar-button
      opacity: 1

  h1, h2
    margin: 8px 0

  .editor-content
    height: 250px
    overflow: auto
    box-shadow: inset 0px -2px 1px 0px rgba(0, 0, 0, 0.05)

    hr
      margin: $gap-medium 0

  table
    width: 100%

  td
    border: 1px solid $color-border
    min-width: 100px

  img
    width: 100%
    max-width: 100%

.ProseMirror-focused
  outline: none

.menubar
  display: flex
  justify-content: center
  flex-wrap: wrap
  border: 1px solid
  padding: 5px
  border-top-left-radius: 3px
  border-top-right-radius: 3px
  border-color: $color-border

  &.is-focused
    .menubar-button
      opacity: 1

.menubar-button
  cursor: pointer
  font-weight: bold
  font-size: 14px
  display: flex
  align-items: center
  justify-content: center
  transition: opacity .2s ease-in-out
  width: 22px
  height: 22px
  margin: 5px
  opacity: 0.25

  @include themify($themes)
    color: themed('color-secondary')

  .sprite
    width: 100%
    height: 100%
    padding: 4px

    @include themify($themes)
      fill: themed('color-secondary')

  &.is-active
    opacity: 1

    @include themify($themes)
      color: themed('color-primary')

    .sprite
      @include themify($themes)
        fill: themed('color-primary')

.editor-content
  border-left: 1px solid
  border-right: 1px solid
  border-bottom: 1px solid
  border-bottom-right-radius: 3px
  border-bottom-left-radius: 3px
  padding: $gap-small
  border-color: $color-border

  table
    border-collapse: collapse
    table-layout: fixed
    width: 100%
    margin: 0
    overflow: hidden

    td
      width: auto!important
      padding: 0 $gap-small

  div
    min-height: 200px
</style>
